import { Observable, ReplaySubject, Subject } from "rxjs";
import {
  addNoteChange,
  WorkflowChange, editNoteChange,
  isAddNoteChange,
  isEditNoteChange,
  isRefreshChange,
  isRemoveNoteChange, refreshNotesChange, removeNoteChange
} from "./WokflowChange";
import { WorkflowRecord, WorkflowRecords } from "../models/WorkflowRecord";
import { WorkflowService } from "../service/WorkflowService";
import { AccountId } from "app/account/types/AccountRecord";

export class WorkflowViewStore {

  private readonly accountId$ = new ReplaySubject<AccountId>(1);
  private readonly changes$ = new Subject<WorkflowChange>();
  private readonly notes$: Observable<WorkflowRecords>;

  public constructor(private readonly service: WorkflowService) {
    this.notes$ = this.buildNotes();
  }

  private buildNotes(): Observable<WorkflowRecords> {
    return this.accountId$
      .switchMap((accountId) => this.watchChanges(accountId))
      .publishReplay(1)
      .refCount();
  }

  private watchChanges(accountId: number): Observable<WorkflowRecords> {
    return this.changes$
      .startWith(refreshNotesChange())
      .concatMap((c) => this.processChange(c, accountId))
      .auditTime(100)
      .flatMap(() => this.retrieveNotes(accountId));
  }

  private processChange(change: WorkflowChange, accountId: number): Observable<boolean> {
    if (isRefreshChange(change)) {
      return Observable.of(true);
    } else if (isAddNoteChange(change)) {
      return Observable.of(change)
        .flatMap((c) => this.service.create(c.content, accountId))
        .map(() => true)
        .catch((err) => Observable.empty());
    } else if (isRemoveNoteChange(change)) {
      return Observable.of(change.id)
        .flatMap((id) => this.service.remove(id, accountId))
        .map(() => true)
        .catch((err) => Observable.empty());
    } else if (isEditNoteChange(change)) {
      return Observable.of(change)
        .flatMap((c) => this.service.update(c.workflowId, accountId, c.workflowRecord))
        .map(() => true)
        .catch((err) => Observable.empty());
    } else {
      return Observable.of(true);
    }
  }

  private retrieveNotes(accountId: number): Observable<WorkflowRecords> {
    return Observable.of(accountId)
      .flatMap((id) => this.service.list(id))
      .catch((err) => Observable.empty());
  }

  public updateAccountId(id: AccountId) {
    this.accountId$.next(id);
  }

  public addNote(content: object) {
    const change = addNoteChange(content);
    this.changes$.next(change);
  }

  public removeNote(id: number) {
    const change = removeNoteChange(id);
    this.changes$.next(change);
  }

  public refreshNotes() {
    const change = refreshNotesChange();
    this.changes$.next(change);
  }

  public editNote(workflowId: number, content: object) {
    const change = editNoteChange(workflowId, content);
    this.changes$.next(change);
  }

  public get notes(): Observable<WorkflowRecords> {
    return this.notes$;
  }
}
